import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefCallback,
} from "react";

interface UseElementOverflowOptions {
  direction?: "vertical" | "horizontal" | "both";
}

interface UseElementOverflowResult<T extends HTMLElement> {
  ref: RefCallback<T>;
  isOverflowing: boolean;
}

const hasOverflow = (
  element: HTMLElement,
  direction: UseElementOverflowOptions["direction"],
) => {
  const vertical = element.scrollHeight > element.clientHeight;
  const horizontal = element.scrollWidth > element.clientWidth;

  if (direction === "horizontal") return horizontal;
  if (direction === "both") return vertical || horizontal;
  return vertical;
};

export function useElementOverflow<T extends HTMLElement>({
  direction = "vertical",
}: UseElementOverflowOptions = {}): UseElementOverflowResult<T> {
  const observedElementRef = useRef<T | null>(null);
  const [observedElement, setObservedElement] = useState<T | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const ref = useCallback((element: T | null) => {
    observedElementRef.current = element;
    setObservedElement(element);
  }, []);

  const updateOverflowState = useCallback(() => {
    const element = observedElementRef.current;
    if (!element) {
      setIsOverflowing(false);
      return;
    }

    setIsOverflowing(hasOverflow(element, direction));
  }, [direction]);

  useLayoutEffect(() => {
    updateOverflowState();
  });

  useEffect(() => {
    if (!observedElement) {
      return;
    }

    const animationFrame = requestAnimationFrame(updateOverflowState);
    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(observedElement);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [observedElement, updateOverflowState]);

  return { ref, isOverflowing };
}
